import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function FaqPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'participation', label: 'Participation' },
    { id: 'experience', label: 'Experience' },
    { id: 'logistics', label: 'Logistics' },
  ];

  const faqs = [
    {
      category: 'participation',
      question: 'Who can attend the summit?',
      answer:
        'The summit is designed for women who allocate capital, steward family legacies, and influence global wealth ecosystems. This includes family office principals, next gen heirs, private equity and venture capital investors, founders and CEOs of high growth companies, stewards of luxury and culture, and advisors who architect cross border wealth strategies. Participation is by application or nomination only.',
    },
    {
      category: 'participation',
      question: 'Is this summit only for participants from the Middle East?',
      answer:
        'No. While the summit takes place in Beirut, it is intentionally global in scope. We welcome participants from Europe, the Gulf, the Levant, North America, Asia, and beyond. The summit reflects the WealthRise Management team\'s cross cultural footprint and is designed to bridge geographies, not limit them.',
    },
    {
      category: 'experience',
      question: 'Can I attend discreetly?',
      answer:
        'Absolutely. Discretion is a core value of this summit. There are no mass stages, no public attendee lists, and no performative networking. All participants are carefully vetted, and the environment is designed for intimate, meaningful conversations in private settings.',
    },
    {
      category: 'participation',
      question: 'Are men allowed to attend?',
      answer:
        'Yes. We welcome the supportive men who stand behind the success of remarkable women — partners, spouses, advisors, and colleagues who champion their achievements and share in their vision. This summit celebrates the power of collaboration and recognizes that true success is often built on mutual support, respect, and shared ambition. Men who embody these values are invited to join select sessions and evening experiences.',
    },
    {
      category: 'logistics',
      question: 'What is the investment to attend?',
      answer:
        'Participation fees and accommodation packages are shared upon acceptance of your application. The investment reflects the curated nature of the summit, the caliber of participants, and the exceptional experiences provided. All fees include access to the full program, signature experiences, meals, and select accommodations.',
    },
    {
      category: 'experience',
      question: 'Will there be media coverage?',
      answer:
        'Yes. A select group of highly prestigious and well-established media partners will be present to document the summit\'s significance. However, all coverage is handled with the utmost discretion and is not publicly shared without explicit consent. This approach allows us to preserve the exclusivity and privacy of the event while capturing its impact for archival and strategic purposes. Attendee identities and private conversations remain confidential, honoring the intimate nature of the gathering.',
    },
    {
      category: 'participation',
      question: 'How do I nominate someone?',
      answer:
        'If you know a woman who would be an exceptional fit for this summit, you can nominate her through the nomination form on our website. Please provide her name, role, organization, and a brief explanation of why you believe she should be considered. All nominations are reviewed by the WealthRise Management team personally.',
    },
    {
      category: 'experience',
      question: 'What should I expect from the experience?',
      answer:
        'Expect intimate, high level conversations with peers who understand your world. Expect frameworks, not just inspiration. Expect to leave with clarity, connections, and actionable insights. And expect an experience that honors your time, intelligence, and discretion.',
    },
  ];

  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="FAQ | Women In Wealth Summit · Beirut 2026"
        description="Frequently asked questions about the Women In Wealth Summit in Beirut. Learn about participation, experience, logistics, media coverage, and what to expect from this exclusive gathering."
        keywords="women in wealth FAQ, summit questions, how to attend, summit participation, summit experience, Beirut summit FAQ"
        canonical={`${siteUrl}/faq`}
        schema={schema}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-champagne-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blush-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative px-8 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
              <i className="ri-question-line text-champagne-400"></i>
              <span className="text-sm text-white/90 font-medium">Frequently Asked Questions</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Everything You Need to Know
            </h1>
            <p className="text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
              Clear answers about participation, experience, and what makes this summit unique.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="px-8 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setExpandedIndex(null);
                  }}
                  className={`px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeCategory === category.id
                      ? 'bg-navy-900 text-white shadow-lg shadow-navy-900/20'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-white">
        <div className="px-8 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <div
                  key={index}
                  className={`group bg-white border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300 ${
                    expandedIndex === index 
                      ? 'shadow-xl shadow-navy-900/5 border-navy-200' 
                      : 'hover:shadow-lg hover:shadow-navy-900/5 hover:border-gray-300'
                  }`}
                >
                  <button
                    onClick={() =>
                      setExpandedIndex(expandedIndex === index ? null : index)
                    }
                    className="w-full px-8 py-7 flex items-start justify-between gap-6 text-left cursor-pointer transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-xs uppercase tracking-wider font-semibold transition-colors ${
                          expandedIndex === index ? 'text-champagne-600' : 'text-gray-400'
                        }`}>
                          {faq.category}
                        </span>
                      </div>
                      <h3 className={`text-xl lg:text-2xl font-serif font-bold transition-colors ${
                        expandedIndex === index ? 'text-navy-900' : 'text-gray-900'
                      }`}>
                        {faq.question}
                      </h3>
                    </div>
                    
                    <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${
                      expandedIndex === index 
                        ? 'bg-navy-900 text-white rotate-180' 
                        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                    }`}>
                      <i className="ri-arrow-down-s-line text-2xl"></i>
                    </div>
                  </button>

                  <div 
                    className={`transition-all duration-500 ease-in-out ${
                      expandedIndex === index 
                        ? 'max-h-96 opacity-100' 
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-8 pb-8">
                      <div className="pt-6 border-t border-gray-100">
                        <p className="text-gray-600 leading-relaxed text-lg">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Media Application Form Section */}
      <section className="py-24 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-champagne-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blush-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative px-8 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
                <i className="ri-camera-lens-line text-champagne-400"></i>
                <span className="text-sm text-white/90 font-medium">Press Credentials</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
                Media Application
              </h2>
              <p className="text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
                For select, prestigious media outlets wishing to cover the summit. All applications are reviewed with discretion.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-12">
              <form 
                id="media-application-form"
                data-readdy-form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                  const originalText = submitBtn.innerHTML;
                  
                  submitBtn.disabled = true;
                  submitBtn.innerHTML = '<i class="ri-loader-4-line animate-spin mr-2"></i>Submitting...';
                  
                  try {
                    const response = await fetch('https://readdy.ai/api/form/d67mln5bd17lru29ki00', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString(),
                    });
                    
                    if (response.ok) {
                      form.reset();
                      submitBtn.innerHTML = '<i class="ri-check-line mr-2"></i>Application Submitted';
                      submitBtn.className = submitBtn.className.replace('bg-champagne-500 hover:bg-champagne-600', 'bg-green-500');
                      setTimeout(() => {
                        submitBtn.innerHTML = originalText;
                        submitBtn.className = submitBtn.className.replace('bg-green-500', 'bg-champagne-500 hover:bg-champagne-600');
                        submitBtn.disabled = false;
                      }, 3000);
                    } else {
                      throw new Error('Submission failed');
                    }
                  } catch (error) {
                    submitBtn.innerHTML = '<i class="ri-error-warning-line mr-2"></i>Error - Try Again';
                    submitBtn.className = submitBtn.className.replace('bg-champagne-500 hover:bg-champagne-600', 'bg-red-500');
                    setTimeout(() => {
                      submitBtn.innerHTML = originalText;
                      submitBtn.className = submitBtn.className.replace('bg-red-500', 'bg-champagne-500 hover:bg-champagne-600');
                      submitBtn.disabled = false;
                    }, 3000);
                  }
                }}
                className="space-y-8"
              >
                {/* Media Outlet Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <i className="ri-building-2-line text-champagne-400"></i>
                    Media Outlet Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Publication / Outlet Name *
                      </label>
                      <input
                        type="text"
                        name="outlet_name"
                        required
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="e.g., Forbes, Financial Times"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Media Type *
                      </label>
                      <select
                        name="media_type"
                        required
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm cursor-pointer"
                      >
                        <option value="" className="bg-navy-900">Select media type</option>
                        <option value="Print Publication" className="bg-navy-900">Print Publication</option>
                        <option value="Digital Publication" className="bg-navy-900">Digital Publication</option>
                        <option value="Broadcast Television" className="bg-navy-900">Broadcast Television</option>
                        <option value="Documentary / Film" className="bg-navy-900">Documentary / Film</option>
                        <option value="Podcast / Audio" className="bg-navy-900">Podcast / Audio</option>
                        <option value="Photography" className="bg-navy-900">Photography</option>
                        <option value="Other" className="bg-navy-900">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <i className="ri-user-line text-champagne-400"></i>
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Role / Title *
                      </label>
                      <input
                        type="text"
                        name="role"
                        required
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="e.g., Senior Editor, Correspondent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>

                {/* Coverage Proposal */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <i className="ri-file-text-line text-champagne-400"></i>
                    Coverage Proposal
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Proposed Coverage Angle *
                      </label>
                      <textarea
                        name="coverage_angle"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm resize-none"
                        placeholder="Describe your proposed coverage angle, story focus, and how it aligns with your publication's audience..."
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Portfolio / Previous Work Links
                      </label>
                      <input
                        type="url"
                        name="portfolio_link"
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="https://yourportfolio.com or relevant article links"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Estimated Reach / Audience Size
                      </label>
                      <input
                        type="text"
                        name="audience_reach"
                        className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-champagne-400 focus:ring-1 focus:ring-champagne-400 transition-all text-sm"
                        placeholder="e.g., 2M monthly readers, 500K subscribers"
                      />
                    </div>
                  </div>
                </div>

                {/* Privacy Agreement */}
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <label className="flex items-start gap-4 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacy_agreement"
                      required
                      className="mt-1 w-5 h-5 rounded border-white/30 bg-white/10 text-champagne-500 focus:ring-champagne-400 cursor-pointer"
                    />
                    <span className="text-sm text-white/70 leading-relaxed">
                      I understand that all coverage must respect the privacy and exclusivity of the event. I agree to submit all content for review before publication and to honor any confidentiality requests from attendees. *
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full py-5 bg-champagne-500 hover:bg-champagne-600 text-navy-900 rounded-xl font-semibold text-lg transition-all shadow-lg shadow-champagne-500/20 hover:shadow-xl hover:shadow-champagne-500/30 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-send-plane-line"></i>
                    Submit Media Application
                  </button>
                </div>
              </form>
              
              <p className="text-center text-white/50 text-sm mt-8">
                Applications are reviewed within 5-7 business days. Only select media partners will be approved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blush-50 via-white to-champagne-50"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-champagne-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blush-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative px-8 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 mb-8">
              <i className="ri-customer-service-line text-champagne-600"></i>
              <span className="text-sm text-gray-700 font-medium">Concierge Support</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
              Still Have Questions?
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-12 max-w-2xl mx-auto">
              Our concierge team is here to help with any additional questions or special requests.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-navy-900 text-white rounded-full font-medium hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 hover:shadow-xl hover:shadow-navy-900/30 whitespace-nowrap cursor-pointer"
              >
                Contact Concierge
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              
              <a
                href="/attend"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-navy-900 rounded-full font-medium hover:bg-gray-50 transition-all border border-gray-200 whitespace-nowrap cursor-pointer"
              >
                Request Invitation
                <i className="ri-mail-send-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
